'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { CLPPurchaseOption, useClpPurchaseContext, useClpSelectedOption } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { BlockList } from '@udemy-v2/react-core-components';
import { useCommonData } from '@udemy-v2/common-data';
import { useClpNoticeContext } from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import { publishEvent } from '@udemy-v2/event-tracking';
import { ExperimentImpressionEvent } from '@udemy-v2/react-experimentation';
import { Icon } from '@udemy-v2/icons';
import styles from './subscription-purchase-content.module.scss';
import { LocalizedHtml, useFormatNumber, useI18n } from '@udemy-v2/i18n';
import { CollapsiblePurchaseSection } from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import { aggregateCollectionStats, FALLBACK_COLLECTION_STATS, PlanPeriod, PlanPeriodStyle, SubscribeFromPageCookie, SubscriptionCTA, SubscriptionCTAClickEvent, SubscriptionLearnMoreEvent, SubscriptionOfferPresented, usePersonalPlanCollectionStats } from '@udemy-v2/react-subscription-browse';
import { SubscriptionPlanProductType } from '@udemy-v2/subscription-graphql';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { BlendedOfferBadge } from '@/features/course-landing-page/components/blended-offer-badge/blended-offer-badge.react-component';
import { LifetimeAccessModal } from '@/features/course-landing-page/components/lifetime-access-modal/lifetime-access-modal.react-component';
import { PersonalPlanIncludesModal } from '@/features/course-landing-page/components/personal-plan-includes-modal/personal-plan-includes-modal.react-component';
import style from '../non-catalog-pp-offer/non-catalog-pp-offer.module.scss';
import { useFeatures } from '@udemy-v2/react-experimentation';
import { CollapsiblePurchaseBoxContent, SUBSCRIPTION_SUB_VALUE_PROP_MSG } from './collapsible-purchase-box-content.react-component';
const UI_REGION = 'purchase_section_tab0';
const PERSONAL_PLAN_COURSE_COUNT = 27000;
const DEFAULT_PERSONAL_PLAN_LEARNER_COUNT = 559000;
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];
interface CategoryStats {
  isSubCopyEnabled?: boolean;
  personalPlanLearnerCount?: number;
}
const CollapsibleSubscriptionPurchaseContentInner = categoryStats => {
  const $ = _c(126);
  const {
    gettext,
    interpolate
  } = useI18n();
  const {
    formatNumber
  } = useFormatNumber();
  const {
    unwrappedConsumerSubscriptionPlan,
    subscriptionCTAConfiguration,
    subscriptionTiersEnabled,
    blendedOfferVariant,
    isEligibleForBlendedOffer,
    blendedOfferFeature,
    clpGenericUdemyPlansCopyEnabled,
    starterPlanUpsellCLPCopyUpdatesEnabled
  } = useClpPurchaseContext();
  const {
    selectedPurchaseOption,
    setSelectedPurchaseOption
  } = useClpSelectedOption();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["change_cta_language_for_pp", "pp_value_prop_update"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t0);
  const changeCtaLangFeatureAssignment = featureVariantAssignments.get("change_cta_language_for_pp")?.configuration as {
    getStarted: boolean;
    getPersonalPlan: boolean;
  } | undefined;
  let t1;
  if ($[1] !== featureVariantAssignments) {
    t1 = featureVariantAssignments.get("pp_value_prop_update")?.configuration;
    $[1] = featureVariantAssignments;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const ppValuePropConfig = t1 as {
    variant1?: boolean;
    variant2?: boolean;
  } | undefined;
  const learnMoreInNewTab = ppValuePropConfig?.variant1 ?? false;
  const learnMoreInModal = ppValuePropConfig?.variant2 ?? false;
  const showPersonalPlanValueProp = learnMoreInNewTab || learnMoreInModal;
  const {
    isCourseInValuePlanCatalog,
    subscribedProductType
  } = useClpSubscriptionContext();
  const isGwgStandaloneSubscriber = subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;
  const course = useClpCourse();
  const commonData = useCommonData();
  const clpNoticeContext = useClpNoticeContext();
  const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;
  const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
  const subscriptionPlanStats = contentCollections ? aggregateCollectionStats(contentCollections.map(_temp)) : FALLBACK_COLLECTION_STATS;
  const personalPlanStats = usePersonalPlanCollectionStats();
  const personalPlanCourseCount = personalPlanStats && "numberOfCourses" in personalPlanStats ? personalPlanStats.numberOfCourses : isGwgStandaloneSubscriber && contentCollections ? subscriptionPlanStats.numberOfCourses : PERSONAL_PLAN_COURSE_COUNT;
  const isPersonalPlanPrimaryPurchaseOptions = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;
  const isStarterCourseInTiers = subscriptionTiersEnabled && isCourseInValuePlanCatalog;
  const hidePersonalPlanValuePropForStarterUpsell = isStarterCourseInTiers && starterPlanUpsellCLPCopyUpdatesEnabled;
  const formattedDiscountMonthlyPrice = unwrappedConsumerSubscriptionPlan?.selectedDiscount?.formattedSubscriptionDiscountMonthlyPrice;
  let t2;
  if ($[3] !== gettext || $[4] !== subscriptionPriceBoxNotice?.subtitle_link_text) {
    t2 = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext("Learn more");
    $[3] = gettext;
    $[4] = subscriptionPriceBoxNotice?.subtitle_link_text;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const learnMoreText = t2;
  const subscriptionContextLearnMoreUrl = unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? "https://udemy.com/personal-plan/";
  const learnMoreUrl = isStarterCourseInTiers ? "https://udemy.com/pricing/" : subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
  const learnMoreTarget = learnMoreInNewTab || subscriptionPriceBoxNotice?.subtitle_link_target === "new_tab" ? "_blank" : "_self";
  let t3;
  if ($[6] !== course.id || $[7] !== learnMoreUrl || $[8] !== unwrappedConsumerSubscriptionPlan) {
    t3 = () => {
      if (SUBSCRIPTION_ATTRIBUTION_PATHS.some(path => learnMoreUrl.includes(path))) {
        SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      }
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionLearnMoreEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          uiRegion: UI_REGION,
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[6] = course.id;
    $[7] = learnMoreUrl;
    $[8] = unwrappedConsumerSubscriptionPlan;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleLearnMoreClick = t3;
  let t4;
  if ($[10] !== course.id || $[11] !== unwrappedConsumerSubscriptionPlan) {
    t4 = () => {
      SubscribeFromPageCookie.set("course_landing_page", parseInt(course.id, 10));
      if (unwrappedConsumerSubscriptionPlan) {
        publishEvent(new SubscriptionCTAClickEvent({
          planId: unwrappedConsumerSubscriptionPlan.id,
          planType: unwrappedConsumerSubscriptionPlan.productType,
          ctaType: unwrappedConsumerSubscriptionPlan.trialPeriodDays ? "trial" : "paid",
          uiRegion: UI_REGION,
          courseId: parseInt(course.id, 10),
          planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds
        }));
      }
    };
    $[10] = course.id;
    $[11] = unwrappedConsumerSubscriptionPlan;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleCtaClick = t4;
  let t5;
  if ($[13] !== commonData?.user?.country || $[14] !== course.frontendTrackingId || $[15] !== course.id || $[16] !== subscriptionTiersEnabled || $[17] !== unwrappedConsumerSubscriptionPlan) {
    t5 = subscriptionTiersEnabled && unwrappedConsumerSubscriptionPlan ? () => {
      publishEvent(new SubscriptionOfferPresented({
        subscriptionPlan: unwrappedConsumerSubscriptionPlan,
        productType: unwrappedConsumerSubscriptionPlan.productType,
        countryCode: commonData?.user?.country ?? "",
        courseId: parseInt(course.id, 10),
        courseTrackingId: course.frontendTrackingId ?? null,
        uiRegion: UI_REGION
      }));
    } : undefined;
    $[13] = commonData?.user?.country;
    $[14] = course.frontendTrackingId;
    $[15] = course.id;
    $[16] = subscriptionTiersEnabled;
    $[17] = unwrappedConsumerSubscriptionPlan;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleOfferImpression = t5;
  let t6;
  if ($[19] !== setSelectedPurchaseOption) {
    t6 = () => setSelectedPurchaseOption(CLPPurchaseOption.SUBSCRIPTION);
    $[19] = setSelectedPurchaseOption;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleExpand = t6;
  const showBlendedOfferBadge = isEligibleForBlendedOffer && !!blendedOfferVariant;
  const [isLifetimeAccessModalOpen, setIsLifetimeAccessModalOpen] = useState(false);
  const [isPersonalPlanIncludesModalOpen, setIsPersonalPlanIncludesModalOpen] = useState(false);
  const hasFiredBlendedOfferImpressionRef = useRef(false);
  let t7;
  if ($[21] !== blendedOfferFeature?.experimentIds || $[22] !== blendedOfferFeature?.isLocalDefault || $[23] !== showBlendedOfferBadge) {
    t7 = () => {
      if (showBlendedOfferBadge && !hasFiredBlendedOfferImpressionRef.current) {
        hasFiredBlendedOfferImpressionRef.current = true;
        publishEvent(new ExperimentImpressionEvent({
          experimentIds: blendedOfferFeature?.experimentIds ?? [],
          isLocalDefaultShown: blendedOfferFeature?.isLocalDefault ?? false
        }));
      }
    };
    $[21] = blendedOfferFeature?.experimentIds;
    $[22] = blendedOfferFeature?.isLocalDefault;
    $[23] = showBlendedOfferBadge;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== blendedOfferFeature || $[26] !== showBlendedOfferBadge) {
    t8 = [showBlendedOfferBadge, blendedOfferFeature];
    $[25] = blendedOfferFeature;
    $[26] = showBlendedOfferBadge;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  useEffect(t7, t8);
  if (!unwrappedConsumerSubscriptionPlan) {
    return null;
  }
  let t9;
  if ($[28] !== gettext) {
    t9 = gettext("Subscribe and save");
    $[28] = gettext;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const titleText = t9;
  let t10;
  if ($[30] !== setIsPersonalPlanIncludesModalOpen) {
    t10 = event => {
      event.stopPropagation();
      setIsPersonalPlanIncludesModalOpen(true);
    };
    $[30] = setIsPersonalPlanIncludesModalOpen;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const openIncludesModal = t10;
  let t11;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = classNames("ud-text-md");
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== titleText) {
    t12 = <span className={t11}>{titleText}</span>;
    $[33] = titleText;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== gettext || $[36] !== learnMoreInModal || $[37] !== openIncludesModal) {
    t13 = learnMoreInModal && <span role="button" tabIndex={0} aria-label={gettext("What's included in Personal Plan")} data-purpose="personal-plan-includes-trigger" className={styles["includes-info-trigger"]} onClick={openIncludesModal} onKeyDown={event_0 => {
      if (event_0.key === "Enter" || event_0.key === " ") {
        event_0.preventDefault();
        openIncludesModal(event_0);
      }
    }}><Icon glyph="info-outline" label="" size="xsmall" /></span>;
    $[35] = gettext;
    $[36] = learnMoreInModal;
    $[37] = openIncludesModal;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t12 || $[40] !== t13) {
    t14 = <span className={styles["title-row"]}>{t12}{t13}</span>;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== blendedOfferVariant || $[43] !== formattedDiscountMonthlyPrice || $[44] !== gettext || $[45] !== showBlendedOfferBadge || $[46] !== unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount || $[47] !== unwrappedConsumerSubscriptionPlan.trialPeriodDays) {
    t15 = showBlendedOfferBadge && blendedOfferVariant === "annual" ? <p><span className="ud-heading-xl">{unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount}</span><span className="ud-text-sm">{gettext("/month, billed yearly")}</span></p> : <PlanPeriod listPriceText={unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount} trialPeriodDays={unwrappedConsumerSubscriptionPlan.trialPeriodDays} style={PlanPeriodStyle.TITLE} discountPriceText={formattedDiscountMonthlyPrice} />;
    $[42] = blendedOfferVariant;
    $[43] = formattedDiscountMonthlyPrice;
    $[44] = gettext;
    $[45] = showBlendedOfferBadge;
    $[46] = unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount;
    $[47] = unwrappedConsumerSubscriptionPlan.trialPeriodDays;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== t14 || $[50] !== t15) {
    t16 = <>{t14}{t15}</>;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  const title = t16;
  let t17;
  if ($[52] !== gettext) {
    t17 = config => {
      if (!config) {
        return gettext("Start subscription");
      }
      if (config.getStarted && !config.getPersonalPlan) {
        return gettext("Get started");
      }
      if (!config.getStarted && config.getPersonalPlan) {
        return gettext("Get Personal Plan");
      }
      return gettext("Start subscription");
    };
    $[52] = gettext;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  const ctaText = t17;
  const T0 = CollapsiblePurchaseSection;
  let t18;
  if ($[54] !== blendedOfferVariant || $[55] !== isLifetimeAccessModalOpen || $[56] !== showBlendedOfferBadge || $[57] !== unwrappedConsumerSubscriptionPlan.urlTermsOfUse) {
    t18 = showBlendedOfferBadge && blendedOfferVariant && <><BlendedOfferBadge variant={blendedOfferVariant} termsUrl={unwrappedConsumerSubscriptionPlan?.urlTermsOfUse} onLifetimeAccessClick={() => setIsLifetimeAccessModalOpen(true)} /><LifetimeAccessModal isOpen={isLifetimeAccessModalOpen} onClose={() => setIsLifetimeAccessModalOpen(false)} /></>;
    $[54] = blendedOfferVariant;
    $[55] = isLifetimeAccessModalOpen;
    $[56] = showBlendedOfferBadge;
    $[57] = unwrappedConsumerSubscriptionPlan.urlTermsOfUse;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  const T1 = BlockList;
  const t19 = "tight";
  const t20 = "small";
  let t21;
  if ($[59] !== categoryStats.isSubCopyEnabled || $[60] !== gettext) {
    t21 = categoryStats.isSubCopyEnabled && <div className={styles["value-prop-badge"]}><Icon className={style["value-prop-icon"]} glyph="info-outline" color="info" size="small" label="" /><LocalizedHtml html={gettext(SUBSCRIPTION_SUB_VALUE_PROP_MSG)} interpolate={{
        bold: <span className="ud-text-bold" />
      }} /></div>;
    $[59] = categoryStats.isSubCopyEnabled;
    $[60] = gettext;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  const t22 = categoryStats.isSubCopyEnabled && <CollapsiblePurchaseBoxContent coursesCount={formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)} categoryName={course.categories[0]?.name} />;
  const t23 = !categoryStats.isSubCopyEnabled && <BlockList.Item icon={<Icon label="" size="xsmall" glyph={showBlendedOfferBadge ? "tick" : "video-playlist"} />}><span className="ud-text-sm">{showBlendedOfferBadge ? interpolate(gettext("Access %(courses_count)s+ Premium courses"), {
        courses_count: formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)
      }, true) : isStarterCourseInTiers && clpGenericUdemyPlansCopyEnabled ? interpolate(gettext("Access up to %(courses_count)s+ courses with a Udemy subscription."), {
        courses_count: formatNumber(personalPlanCourseCount)
      }, true) : interpolate(gettext("Access to %(courses_count)s+ top-rated courses"), {
        courses_count: formatNumber(isStarterCourseInTiers ? subscriptionPlanStats.numberOfCourses : subscriptionTiersEnabled ? personalPlanCourseCount : subscriptionPlanStats.numberOfCourses)
      }, true)}</span></BlockList.Item>;
  const t24 = showBlendedOfferBadge ? "tick" : subscriptionTiersEnabled ? "cancel" : "coupon";
  let t25;
  if ($[62] !== t24) {
    t25 = <Icon label="" size="xsmall" glyph={t24} />;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== gettext) {
    t26 = gettext("Cancel anytime");
    $[64] = gettext;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t26) {
    t27 = <span className="ud-text-sm">{t26}</span>;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t25 || $[69] !== t27) {
    t28 = <BlockList.Item icon={t25}>{t27}</BlockList.Item>;
    $[68] = t25;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== categoryStats.isSubCopyEnabled || $[72] !== gettext || $[73] !== handleLearnMoreClick || $[74] !== learnMoreInModal || $[75] !== learnMoreInNewTab || $[76] !== learnMoreTarget || $[77] !== learnMoreText || $[78] !== learnMoreUrl || $[79] !== showBlendedOfferBadge) {
    t29 = !categoryStats.isSubCopyEnabled && !learnMoreInModal && <BlockList.Item icon={<Icon label="" size="xsmall" glyph="info-outline" />}><a className={learnMoreInNewTab ? styles["learn-more-link-default"] : showBlendedOfferBadge ? styles["learn-more-link-blended"] : styles["learn-more-link"]} data-purpose="subscription-learn-more-link" href={learnMoreUrl} target={learnMoreTarget} onClick={handleLearnMoreClick}>{learnMoreInNewTab ? gettext("Learn more about Udemy subscriptions") : showBlendedOfferBadge ? gettext("Learn more about Personal Plan") : learnMoreText}</a></BlockList.Item>;
    $[71] = categoryStats.isSubCopyEnabled;
    $[72] = gettext;
    $[73] = handleLearnMoreClick;
    $[74] = learnMoreInModal;
    $[75] = learnMoreInNewTab;
    $[76] = learnMoreTarget;
    $[77] = learnMoreText;
    $[78] = learnMoreUrl;
    $[79] = showBlendedOfferBadge;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== T1 || $[82] !== t21 || $[83] !== t22 || $[84] !== t23 || $[85] !== t28 || $[86] !== t29) {
    t30 = <T1 padding={t19} size={t20}>{t21}{t22}{t23}{t28}{t29}</T1>;
    $[81] = T1;
    $[82] = t21;
    $[83] = t22;
    $[84] = t23;
    $[85] = t28;
    $[86] = t29;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  const T2 = SubscriptionCTA;
  let t31;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = classNames(styles["subscription-cta"], styles["subscription-cta-collapsible"]);
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  const t32 = UI_REGION;
  const t33 = !hidePersonalPlanValuePropForStarterUpsell && (showPersonalPlanValueProp || categoryStats.isSubCopyEnabled);
  const t34 = "subscription-purchase-button";
  const t35 = "primary";
  const t36 = parseInt(course.id, 10);
  const t37 = false;
  let t38;
  if ($[89] !== subscriptionCTAConfiguration || $[90] !== subscriptionPriceBoxNotice) {
    t38 = subscriptionCTAConfiguration?.upgradeUrl ? {
      ...subscriptionPriceBoxNotice,
      cta_1_url: subscriptionCTAConfiguration.upgradeUrl
    } : subscriptionPriceBoxNotice;
    $[89] = subscriptionCTAConfiguration;
    $[90] = subscriptionPriceBoxNotice;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  const t39 = subscriptionCTAConfiguration?.directCheckout;
  const t40 = subscriptionCTAConfiguration?.selectPlanText;
  let t41;
  if ($[92] !== categoryStats.personalPlanLearnerCount || $[93] !== formatNumber || $[94] !== gettext || $[95] !== interpolate || $[96] !== showPersonalPlanValueProp) {
    t41 = showPersonalPlanValueProp ? interpolate(gettext("Join %(learner_count)s learners on Personal Plan"), {
      learner_count: formatNumber(categoryStats.personalPlanLearnerCount ?? DEFAULT_PERSONAL_PLAN_LEARNER_COUNT)
    }, true) : "Join 5 lakh+ learners on Personal Plan today";
    $[92] = categoryStats.personalPlanLearnerCount;
    $[93] = formatNumber;
    $[94] = gettext;
    $[95] = interpolate;
    $[96] = showPersonalPlanValueProp;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  const t42 = subscriptionCTAConfiguration?.purchaseCtaText || ctaText(changeCtaLangFeatureAssignment);
  let t43;
  if ($[98] !== T2 || $[99] !== handleCtaClick || $[100] !== handleOfferImpression || $[101] !== t31 || $[102] !== t32 || $[103] !== t33 || $[104] !== t36 || $[105] !== t38 || $[106] !== t39 || $[107] !== t40 || $[108] !== t41 || $[109] !== t42 || $[110] !== unwrappedConsumerSubscriptionPlan) {
    t43 = <T2 className={t31} uiRegion={t32} showSubtitle={t33} data-purpose={t34} offerCtaStyle={t35} onCTAClick={handleCtaClick} trackImpression={handleOfferImpression} courseId={t36} includeTrialDaysOnCta={t37} plan={unwrappedConsumerSubscriptionPlan} noticeData={t38} directCheckout={t39} selectPlanText={t40} customSubtitle={t41} purchaseCtaText={t42} />;
    $[98] = T2;
    $[99] = handleCtaClick;
    $[100] = handleOfferImpression;
    $[101] = t31;
    $[102] = t32;
    $[103] = t33;
    $[104] = t36;
    $[105] = t38;
    $[106] = t39;
    $[107] = t40;
    $[108] = t41;
    $[109] = t42;
    $[110] = unwrappedConsumerSubscriptionPlan;
    $[111] = t43;
  } else {
    t43 = $[111];
  }
  let t44;
  if ($[112] !== isPersonalPlanIncludesModalOpen || $[113] !== learnMoreInModal || $[114] !== personalPlanCourseCount || $[115] !== setIsPersonalPlanIncludesModalOpen) {
    t44 = learnMoreInModal && <PersonalPlanIncludesModal isOpen={isPersonalPlanIncludesModalOpen} onClose={() => setIsPersonalPlanIncludesModalOpen(false)} courseCount={personalPlanCourseCount} />;
    $[112] = isPersonalPlanIncludesModalOpen;
    $[113] = learnMoreInModal;
    $[114] = personalPlanCourseCount;
    $[115] = setIsPersonalPlanIncludesModalOpen;
    $[116] = t44;
  } else {
    t44 = $[116];
  }
  let t45;
  if ($[117] !== T0 || $[118] !== handleExpand || $[119] !== isPersonalPlanPrimaryPurchaseOptions || $[120] !== t18 || $[121] !== t30 || $[122] !== t43 || $[123] !== t44 || $[124] !== title) {
    t45 = <T0 expanded={isPersonalPlanPrimaryPurchaseOptions} title={title} onExpand={handleExpand}>{t18}{t30}{t43}{t44}</T0>;
    $[117] = T0;
    $[118] = handleExpand;
    $[119] = isPersonalPlanPrimaryPurchaseOptions;
    $[120] = t18;
    $[121] = t30;
    $[122] = t43;
    $[123] = t44;
    $[124] = title;
    $[125] = t45;
  } else {
    t45 = $[125];
  }
  return t45;
};
export const CollapsibleSubscriptionPurchaseContent = React.memo(CollapsibleSubscriptionPurchaseContentInner);
function _temp(contentCollection) {
  return contentCollection.stats;
}